import { PaginatedApi } from "../../components/organisms/CustomTable";
import { useAppSelector } from "../../hooks/redux";
import api, { API_URL } from "./api";
import { useGet } from "./useGet";

const medicalProviderApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProviders: build.query<any, string>({
      query: (query = "") => `providers?${query}`,
      providesTags: (result, error, arg) => [{ type: "Providers", id: "LIST" }],
    }),
    // Paginated search endpoint for table with proper caching
    searchProvidersPaginated: build.query<
      { providers: any[]; total: number },
      {
        filter: string;
        page: number;
        pageSize: number;
        sort?: { id: string; desc: boolean } | null;
        search?: string;
      }
    >({
      query: ({ filter, page, pageSize, sort, search }) => {
        const take = pageSize || 100;
        const skip = page * pageSize || 0;
        const orderBy = sort?.id;
        const desc = sort?.desc ? "DESC" : "ASC";
        const sortParam = orderBy ? `&orderBy=${orderBy}&desc=${desc}` : "";
        const searchParam = search ? `&name=${encodeURIComponent(search)}` : "";
        return `providers?${filter}${searchParam}&take=${take}&skip=${skip}${sortParam}`;
      },
      providesTags: (result) =>
        result?.providers
          ? [
              ...result.providers.map(({ id }) => ({
                type: "Providers" as const,
                id,
              })),
              { type: "Providers", id: "LIST" },
            ]
          : [{ type: "Providers", id: "LIST" }],
    }),
    updateProvider: build.mutation({
      query: (provider) => ({
        url: `providers/${provider.id}`,
        method: `PUT`,
        body: provider,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [
          { type: "Providers", id },
          { type: "Providers", id: "LIST" },
          { type: "Requests", id: "LIST" },
        ];
      },
    }),
    getProvider: build.query({
      query: (id) => `providers/${id}`,
      providesTags: (result, error, id) => [{ type: "Providers", id }],
    }),
    // Silent version for audit logs - returns null on error instead of showing toast
    getProviderSilent: build.query({
      queryFn: async (id, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery(`providers/${id}`);
        if (result.error) {
          return { data: null };
        }
        return { data: result.data };
      },
    }),
    getProviderRequests: build.query({
      query: (id) => `/providers/${id}/requests`,
      providesTags: (result, error, id) => [
        { type: "Providers", id },
        { type: "Requests", id: "LIST" },
      ],
    }),
    deleteProvider: build.mutation({
      query: (id) => ({
        url: `providers/${id}`,
        method: `DELETE`,
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Providers", id },
        { type: "Providers", id: "LIST" },
      ],
    }),
    getProviderConnections: build.query({
      query: (id) => `/providers/${id}/connections`,
      providesTags: (result, error, id) => [
        { type: "Providers", id },
        { type: "Providers", id: "LIST" },
      ],
    }),
    updateProviderConnection: build.mutation({
      query: (body) => ({
        url: `providers/connections`,
        method: `POST`,
        body: body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Providers", id },
        { type: "Providers", id: "LIST" },
      ],
    }),
    deleteProviderConnection: build.mutation({
      query: (body) => ({
        url: `/providers/${body.id}/connections/${body.connectionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Providers", id },
        { type: "Providers", id: "LIST" },
      ],
    }),
    createProvider: build.mutation({
      query: (provider) => ({
        url: `providers`,
        method: `POST`,
        body: provider,
      }),
      invalidatesTags: [{ type: "Providers", id: "LIST" }],
    }),
    mergeProviders: build.mutation({
      query: (body) => ({
        url: `providers/merge`,
        method: `POST`,
        body,
      }),
      invalidatesTags: (result, error, { targetId }) => [
        { type: "Providers", id: targetId },
        { type: "Providers", id: "LIST" },
        { type: "Requests", id: "LIST" },
      ],
    }),
    bulkCreateConnections: build.mutation<
      {
        status: string;
        created: number;
        updated: number;
        succeeded: string[];
        failed: { id: string; error: string }[];
      },
      {
        fromId: string;
        toIds: string[];
        connectionNotes?: string;
        requestType?: string;
        validFrom?: string;
        validTo?: string;
      }
    >({
      query: (body) => ({
        url: `providers/connections/bulk`,
        method: `POST`,
        body,
      }),
      invalidatesTags: (result, error, { fromId }) => [
        { type: "Providers", id: fromId },
        { type: "Providers", id: "LIST" },
      ],
    }),
    pushNoteToConnected: build.mutation<
      {
        status: string;
        updated: number;
        total: number;
        failed: { id: string; error: string }[];
      },
      {
        providerId: string;
        noteField:
          | "notes"
          | "connectionNotes"
          | "followUpNotes"
          | "vettingNotes";
        noteContent: string;
        appendMode: boolean;
        connectionIds?: string[];
      }
    >({
      query: ({ providerId, ...body }) => ({
        url: `providers/${providerId}/push-note-to-connected`,
        method: `POST`,
        body,
      }),
      invalidatesTags: () => [{ type: "Providers" }],
    }),
  }),
});

export const useSearchProvidersByName = (verifiedOnly?: boolean) => {
  const token = useAppSelector((s) => s.auth.token);
  return async (name: string) =>
    await fetch(
      `${API_URL}/providers-search?name=${name}${
        verifiedOnly ? `&verifiedOnly=true` : ""
      }`,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    ).then((res) => res.json());
};

export const {
  useGetProvidersQuery,
  useSearchProvidersPaginatedQuery,
  useGetProviderRequestsQuery,
  useUpdateProviderMutation,
  useGetProviderQuery,
  useGetProviderSilentQuery,
  useDeleteProviderMutation,
  useGetProviderConnectionsQuery,
  useUpdateProviderConnectionMutation,
  useDeleteProviderConnectionMutation,
  useCreateProviderMutation,
  useMergeProvidersMutation,
  useBulkCreateConnectionsMutation,
  usePushNoteToConnectedMutation,
} = medicalProviderApi;

export const useGetMedicalProviders = (filter): PaginatedApi<any> => {
  const get = useGet();
  return async (page, pageSize, sort, searchString) => {
    const take = pageSize || 100;
    const skip = page * pageSize || 0;
    const selectedSort =
      sort && Array.isArray(sort) && sort.length > 0 ? sort[0] : null;
    const orderBy = selectedSort?.id;
    const desc = selectedSort?.desc ? "DESC" : "ASC";

    const response = await get(
      `${API_URL}/providers?${filter}${
        searchString ? `&name=${searchString}` : ""
      }&take=${take}&skip=${skip}${
        orderBy ? `&orderBy=${orderBy}&desc=${desc}` : ""
      }`
    );
    const json = await response.json();
    const { providers, total } = json;
    return {
      results: providers,
      pageCount: Math.ceil(total / pageSize),
      total,
    };
  };
};
